import axios from 'axios';
import TelegramWebApp from '../telegram-init'; 
import type { User } from '../types/user';

interface APIError {
  message?: string;
  code?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      [key: string]: unknown;
    };
  };
}

export interface PaymentDetails {
  subscription_period: string;
  payment_provider: 'cryptomus';
  cryptomus_payment_id?: string;
  payment_url?: string;
  network?: string;
  address?: string;
  payment_status?: string;
  payment_amount?: string;
  from?: string;
  merchant_amount?: string;
}

export interface CryptoPaymentResponse {
  id: string;
  user_id: string;
  payment_method_id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_details: PaymentDetails;
}

export interface SubscriptionResponse {
  is_active: boolean;
  subscription_expires_at: string | null;
  subscription_type: string | null;
  subscription_start_date: string | null;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4888';


// Флаг для отслеживания показа ошибки подключения
let connectionErrorShown = false;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем интерцептор для автоматического добавления initData
api.interceptors.request.use(
  (config) => {
    try {
      const initData = TelegramWebApp.getInitData();
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['x-telegram-init-data'] = initData;
    } catch (error) {
      console.warn('[API] Failed to get initData:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Функция для перенаправления на страницу ошибки
const redirectToError = (message: string) => {
  // Используем window.location для навигации, так как useNavigate доступен только в компонентах
  window.location.href = `/error?message=${encodeURIComponent(message)}`;
};

// Добавляем интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorMessage = '';

    // Проверяем наличие ответа от сервера
    if (error.response) {
      // Если сервер вернул ошибку с сообщением
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else {
        // Если сервер вернул ошибку без сообщения
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid request. Please check your data.';
            break;
          case 401:
            errorMessage = 'Authentication failed. Please try again.';
            break;
          case 403:
            errorMessage = 'Access denied. Please check your permissions.';
            break;
          case 404:
            errorMessage = 'Resource not found.';
            break;
          case 500:
            errorMessage = 'SERVICE: Server error. Please try again later.';
            redirectToError(errorMessage);
            break;
          case 502:
          case 503:
          case 504:
            errorMessage = 'SERVICE: Service is temporarily unavailable.';
            redirectToError(errorMessage);
            break;
          default:
            errorMessage = 'An error occurred. Please try again.';
        }
      }
    } else if (error.request) {
      // Если запрос был сделан, но ответ не получен
      const isCorsError = error.message?.includes('access control checks') ||
                         error.message?.includes('CORS') ||
                         error.message?.includes('XMLHttpRequest cannot load');
      
      const isNetworkError = error.code === 'ERR_NETWORK' || 
                             error.message?.includes('Network Error') ||
                             error.message?.includes('Failed to fetch') ||
                             error.message?.includes('Could not connect');
      
      if (isCorsError) {
        errorMessage = 'CORS: Server configuration error. Please check CORS settings on the server.';
      } else if (isNetworkError) {
        errorMessage = 'NETWORK: Connection error. Please check your internet connection and server availability.';
      } else {
        errorMessage = 'NETWORK: Request timeout or server unreachable.';
      }
    } else {
      // Если произошла ошибка при настройке запроса
      errorMessage = error.message || 'Request failed. Please try again.';
    }

    error.message = errorMessage;
    return Promise.reject(error);
  }
);

const handleError = (error: APIError) => {
  console.error('[API] Error:', {
    message: error.message,
    status: error.response?.status,
    data: error.response?.data
  });
};

// Валидация пользователя Telegram
export const validateTelegramUser = async (): Promise<User> => {
  const maxRetries = 3;
  const retryDelay = 1000; // 1 секунда
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const rawInitData = window.Telegram?.WebApp?.initDataUnsafe;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const refcode = rawInitData?.start_param ?? null;

      const response = await api.post<User>(
        '/api/v1/auth/validate-telegram-user',
        {
          timezone,
          refcode
        },
        {
          timeout: 10000, // 10 секунд таймаут
        }
      );
      
      connectionErrorShown = false;
      return response.data;
    } catch (error) {
      const apiError = error as APIError;
      const isNetworkError = apiError.code === 'ERR_NETWORK' || 
                            apiError.message?.includes('Network Error') ||
                            apiError.message?.includes('Failed to fetch') ||
                            apiError.message?.includes('Could not connect') ||
                            apiError.message?.includes('timeout') ||
                            apiError.message?.includes('access control checks');
      
      // Логируем только при последней попытке
      if (attempt === maxRetries) {
        console.error(`[Auth] Validation failed after ${maxRetries} attempts:`, {
          message: apiError.message,
          status: apiError.response?.status,
          code: apiError.code
        });
      }
      
      // Если это не сетевая ошибка или последняя попытка, выбрасываем ошибку
      if (!isNetworkError || attempt === maxRetries) {
        // Показываем ошибку только при последней попытке и если еще не показывали
        if (attempt === maxRetries && !connectionErrorShown) {
          connectionErrorShown = true;
          
          const isCorsError = apiError.message?.includes('access control checks') || 
                             apiError.message?.includes('CORS') ||
                             apiError.message?.includes('XMLHttpRequest cannot load');
          
          const popupMessage = isCorsError
            ? `Server at ${API_URL} is not responding or CORS is not configured properly.\n\nPlease ensure:\n1. Backend server is running on port 4888\n2. CORS is properly configured in the backend\n3. Server is accessible from this device`
            : `Cannot connect to server at ${API_URL}.\n\nPlease ensure the backend server is running and accessible.`;
          
          try {
            TelegramWebApp.showPopup({
              title: 'Server Connection Error',
              message: popupMessage,
              buttons: [{ type: 'default', text: 'OK' }]
            });
          } catch (popupError) {
            console.warn('[Auth] Failed to show error popup:', popupError);
          }
        }
        
        throw error;
      }
      
      // Ждем перед повторной попыткой
      await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
    }
  }
  
  throw new Error('Failed to validate user after multiple attempts');
};

// Получение данных профиля
export const getProfile = async () => {
  try {
    const response = await api.get('/api/v1/profile');
    return response.data;
  } catch (error) {
    handleError(error as APIError);
    throw error;
  }
};

// Получение статистики реферальной программы
export const getReferralStats = async (userId: string) => {
  try {
    const response = await api.get(`/api/v1/referrals/stats/${userId}`);
    return response.data;
  } catch (error) {
    handleError(error as APIError);
    throw error;
  }
};

// Создание реферальной связи
export const createReferral = async (referralCode: string) => {
  try {
    const response = await api.post('/api/v1/referrals', {
      referral_code: referralCode
    });
    return response.data;
  } catch (error) {
    handleError(error as APIError);
    throw error;
  }
};

// Генерация реферального кода
export const generateReferralCode = async () => {
  try {
    const response = await api.post('/api/v1/referrals/generate-code');
    return response.data;
  } catch (error) {
    console.error('[API] Error generating referral code:', error);
    throw error;
  }
};

// Получение информации о подписке
export const getSubscriptionInfo = async (): Promise<SubscriptionResponse> => {
  try {
    const response = await api.get<SubscriptionResponse>('/api/v1/users/profile/subscription');
    return response.data;
  } catch (error) {
    console.error('[API] Error getting subscription info:', error);
    throw error;
  }
};

export default api;
